import { Contact, ContactInput, ContactsResponse, Category, CategoryInput, CategoriesResponse, ImportResult, CSVImportOptions, EtiquetasResponse, Tenant, CanalTemplate } from '../types';

const API_BASE_URL = '/api';

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');

    const headers: HeadersInit = {
      ...options?.headers,
    };

    // Só adicionar Content-Type JSON se não for FormData
    // FormData define seu próprio Content-Type com boundary
    if (!(options?.body instanceof FormData)) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Enviar tenant selecionado para SuperAdmin (multitenancy)
    const selectedTenantId = localStorage.getItem('selected_tenant_id');
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
        const quotaError = new Error(error.message || error.error || 'Limite atingido');
        (quotaError as any).isQuotaError = true;
        (quotaError as any).upgradeRequired = true;
        throw quotaError;
      }

      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  // Generic POST method
  async post<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      // Se for FormData, não fazer stringify
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      ...options,
    });
  }

  // Generic GET method
  async get<T = any>(endpoint: string, options?: { params?: Record<string, string> }): Promise<T> {
    let url = endpoint;
    
    // Adicionar query params se fornecidos
    if (options?.params) {
      const searchParams = new URLSearchParams(options.params);
      const queryString = searchParams.toString();
      if (queryString) {
        url = `${endpoint}?${queryString}`;
      }
    }
    
    return this.request<T>(url, {
      method: 'GET',
    });
  }

  // Generic DELETE method
  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // Generic PATCH method
  async patch<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      ...options,
    });
  }

  // Generic PUT method
  async put<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      ...options,
    });
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    categoryId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.categoryId) searchParams.set('categoriaId', params.categoryId);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>('/contatos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  async deleteContacts(ids: string[]): Promise<{ deleted: number }> {
    return this.request<{ deleted: number }>('/contatos/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getCategoriesWithCount(): Promise<{
    categories: Array<Category & { contactCount: number }>;
    uncategorizedCount: number;
  }> {
    return this.request('/categorias/with-count');
  }

  async getAllCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categorias/all');
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>('/categorias', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  // CSV Import methods
  async importCSV(file: File, categoryOptions?: string | CSVImportOptions): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const options: CSVImportOptions | undefined = typeof categoryOptions === 'string'
      ? { categoryMode: 'existing', defaultCategoryId: categoryOptions }
      : categoryOptions;
    
    // Adicionar opções de categoria se fornecidas
    if (options?.categoryMode) {
      formData.append('categoryMode', options.categoryMode);
    }
    if (options?.defaultCategoryId) {
      formData.append('defaultCategoryId', options.defaultCategoryId);
    }
    if (options?.newCategoryName) {
      formData.append('newCategoryName', options.newCategoryName);
    }
    if (options?.splitEnabled !== undefined) {
      formData.append('splitEnabled', String(options.splitEnabled));
    }
    if (options?.splitSize) {
      formData.append('splitSize', String(options.splitSize));
    }

    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  async exportCSV(categoriaId?: string): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    const params = new URLSearchParams();
    if (categoriaId) params.set('categoriaId', categoriaId);
    const queryString = params.toString();
    const url = `${API_BASE_URL}/contatos/export${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`Erro ao exportar: HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;

    // Extrair filename do header ou usar padrão
    const disposition = response.headers.get('Content-Disposition');
    const filenameMatch = disposition?.match(/filename="?(.+?)"?$/);
    a.download = filenameMatch?.[1] || `contatos_${new Date().toISOString().split('T')[0]}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Tenant methods
  async getCurrentTenant(): Promise<Tenant> {
    return this.request<Tenant>('/tenants/current');
  }

  // Etiquetas externas
  async getEtiquetas(plataforma: string, accountId: string): Promise<EtiquetasResponse> {
    const response = await fetch('https://api.konverte.digital/webhook/etiquetas/plataformas', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        plataforma: plataforma,
        funcao: 'etiquetas',
        assignee_type: 'all',
        status: 'open',
        account_id: accountId
      })
    });

    if (!response.ok) {
      throw new Error('Erro ao buscar etiquetas da plataforma');
    }

    return response.json();
  }

  async getCaixasEntrada(plataforma: string, accountId: string): Promise<CanalTemplate[]> {
    const response = await fetch('https://api.konverte.digital/webhook/disparador/caixas/entrada', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        plataforma: plataforma,
        account_id: accountId,
        tipo_whatsapp: 'oficial'
      })
    });

    if (!response.ok) {
      throw new Error('Erro ao buscar caixas de entrada da API Oficial');
    }

    const data = await response.json();
    
    // Log sensível removido por segurança (contém provider_config com API keys)
    // console.log('🔍 Caixas da API Oficial recebidas:', Array.isArray(data) ? data.length : 'estrutura aninhada');
    
    /**
     * Estrutura esperada da API:
     * [
     *   {
     *     "canais_templates": [
     *       { id, name, phone_number, message_templates, provider_config, ... },
     *       { ... }
     *     ]
     *   }
     * ]
     * 
     * Cada canal tem:
     * - id, name, channel_id, channel_type, phone_number, has_templates
     * - message_templates: array de templates
     * - provider_config: { api_key, phone_number_id, business_account_id }
     */
    
    if (Array.isArray(data)) {
      // Estrutura padrão: Array com objeto contendo canais_templates
      if (data.length > 0 && data[0].canais_templates) {
        // console.log(`✅ Extraindo ${data[0].canais_templates.length} caixas de entrada`);
        return data[0].canais_templates;
      }
      // Fallback: Array direto de caixas
      // console.log('🔍 Retornando array direto');
      return data;
    } else if (data.canais_templates) {
      // Fallback: Objeto direto com canais_templates
      // console.log('🔍 Extraindo canais_templates do objeto');
      return data.canais_templates;
    }
    
    // Estrutura não reconhecida
    console.error('⚠️ Estrutura não reconhecida:', data);
    return [];
  }
}

export const apiService = new ApiService();
export default apiService;
